import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ClipboardList, Package, Search, RefreshCw, Printer, Play, Clock, CircleCheck as CheckCircle2, TrendingUp, Filter, ArrowUpDown, Calendar, Loader as Loader2, TriangleAlert as AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import type { ShopifyOrder } from '../types/shopify';
import { getRelativeTime, getFulfillmentDisplayLabel, getFulfillmentDisplayColor } from '../types/shopify';
import { checkShopifyOrderLiveStatus } from '../utils/shopifyOrderLiveStatus';
import { getPickableLineItems, getPickableLineCount, getPickableUnitCount } from '../utils/pickableLineItems';
import { getAdminId } from '../utils/getAdminId';
import { PickModal } from '../components/worker/PickModal';
import { PickListPrintable } from '../components/shopify/PickListPrintable';
import { ItemCountFilterBar } from '../components/worker/ItemCountFilterBar';
import { useWorkerOrders } from '../hooks/useWorkerOrders';
import { useOrderCountsBySize } from '../hooks/useOrderCountsBySize';
import type { WorkerOrder, SortOption, ItemCountFilter, WorkerOrderLineItem } from '../types/worker';
import type { ShopifyOrderLineItem } from '../types/shopify';
import { ITEM_COUNT_FILTER_OPTIONS } from '../types/worker';

function toWorkerLineItems(items: ShopifyOrderLineItem[]): WorkerOrderLineItem[] {
  return items.map((item) => ({
    sku: item.sku,
    title: item.title,
    quantity: item.quantity,
    price: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
    fulfillable_quantity: item.fulfillable_quantity,
  }));
}

interface TerminalStats {
  pickedToday: number;
  pickedYesterday: number;
  totalToPick: number;
  pickPercentage: number;
}

type FilterOption = 'all' | 'ready_to_pick';

export const WorkerTerminal: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<TerminalStats>({
    pickedToday: 0,
    pickedYesterday: 0,
    totalToPick: 0,
    pickPercentage: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('oldest');
  const [itemCountFilter, setItemCountFilter] = useState<ItemCountFilter>('all');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<WorkerOrder | null>(null);
  const [pickModalOpen, setPickModalOpen] = useState(false);
  const [printOrder, setPrintOrder] = useState<WorkerOrder | null>(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [claimingOrderId, setClaimingOrderId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);

  const adminId = useMemo(() => {
    if (!user) return null;
    return getAdminId(user);
  }, [user]);

  const {
    orders,
    loading,
    error: ordersError,
    totalCount,
    hasMore,
    loadOrders,
    loadMore,
    refresh: refreshOrders,
    clearError,
  } = useWorkerOrders({
    adminId,
    pageSize: 25,
    sortBy,
    itemCountFilter,
  });

  const {
    counts: orderCounts,
    loading: countsLoading,
    refresh: refreshCounts,
  } = useOrderCountsBySize({ adminId });

  const loadStats = useCallback(async () => {
    if (!adminId) return;
    setStatsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_worker_terminal_stats', {
        p_admin_id: adminId,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setStats({
          pickedToday: data[0].picked_today || 0,
          pickedYesterday: data[0].picked_yesterday || 0,
          totalToPick: data[0].total_to_pick || 0,
          pickPercentage: data[0].pick_percentage || 0,
        });
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    loadStats();
    loadOrders();
    refreshCounts();
  }, [loadStats, loadOrders, refreshCounts]);

  useEffect(() => {
    if (!loading) {
      setLastUpdated(new Date());
    }
  }, [loading, orders]);

  const handleRefresh = async () => {
    setRefreshing(true);
    clearError();
    await Promise.all([loadStats(), refreshOrders(), refreshCounts()]);
    setRefreshing(false);
  };

  const handleFilterChange = (filter: ItemCountFilter) => {
    setItemCountFilter(filter);
  };

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (order) =>
          order.order_number.toLowerCase().includes(query) ||
          (order.customer_name && order.customer_name.toLowerCase().includes(query)) ||
          (order.customer_email && order.customer_email.toLowerCase().includes(query)) ||
          order.line_items?.some(
            (item) =>
              item.sku?.toLowerCase().includes(query) ||
              item.title.toLowerCase().includes(query)
          )
      );
    }

    return result;
  }, [orders, searchQuery]);

  const getTotalUnits = (order: WorkerOrder) => {
    return getPickableUnitCount(order.line_items || []);
  };

  const getLineItemCount = (order: WorkerOrder) => {
    return getPickableLineCount(order.line_items || []);
  };

  const handleStartPick = async (order: WorkerOrder) => {
    if (!user || !adminId || claimingOrderId) return;

    setClaimingOrderId(order.id);
    setClaimError(null);

    try {
      const liveStatus = await checkShopifyOrderLiveStatus(order.shopify_order_id);
      if (liveStatus.error) {
        setClaimError(liveStatus.error);
        setTimeout(() => setClaimError(null), 4000);
        return;
      }
      if (liveStatus.cancelled) {
        setClaimError(`Order ${order.order_number} was cancelled in Shopify and removed from the queue.`);
        await Promise.all([refreshOrders(), refreshCounts()]);
        setTimeout(() => setClaimError(null), 6000);
        return;
      }

      const orderForPick: WorkerOrder = liveStatus.lineItems?.length
        ? {
            ...order,
            line_items: toWorkerLineItems(liveStatus.lineItems),
            fulfillment_status: liveStatus.fulfillmentStatus ?? order.fulfillment_status,
          }
        : order;

      const { data, error } = await supabase.rpc('claim_order_for_picking', {
        p_order_id: order.id,
        p_admin_id: adminId,
        p_user_id: user.id,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        setClaimError(result.error || 'Could not claim this order');
        await Promise.all([refreshOrders(), refreshCounts()]);
        setTimeout(() => setClaimError(null), 4000);
        return;
      }

      setSelectedOrder(orderForPick);
      setPickModalOpen(true);
    } catch (err) {
      setClaimError('Failed to claim order. Please try again.');
      setTimeout(() => setClaimError(null), 4000);
    } finally {
      setClaimingOrderId(null);
    }
  };

  const handleReleaseClaim = async (orderId: string) => {
    if (!user || !adminId) return;
    try {
      await supabase.rpc('release_order_claim', {
        p_order_id: orderId,
        p_admin_id: adminId,
        p_user_id: user.id,
      });
    } catch {}
  };

  const handlePickComplete = async () => {
    setPickModalOpen(false);
    setSelectedOrder(null);
    await Promise.all([loadStats(), refreshOrders(), refreshCounts()]);
  };

  const handlePrintPickSheet = async (order: WorkerOrder) => {
    try {
      const liveStatus = await checkShopifyOrderLiveStatus(order.shopify_order_id);
      if (liveStatus.cancelled) {
        setClaimError(`Order ${order.order_number} was cancelled in Shopify and removed from the queue.`);
        await Promise.all([refreshOrders(), refreshCounts()]);
        setTimeout(() => setClaimError(null), 6000);
        return;
      }

      const printReadyOrder: WorkerOrder = liveStatus.lineItems?.length
        ? {
            ...order,
            line_items: toWorkerLineItems(liveStatus.lineItems),
            fulfillment_status: liveStatus.fulfillmentStatus ?? order.fulfillment_status,
          }
        : order;

      setPrintOrder(printReadyOrder);
    } catch {
      setPrintOrder(order);
    }
  };

  const displayedCount = filteredOrders.length;
  const showingFrom = displayedCount > 0 ? 1 : 0;
  const showingTo = displayedCount;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <ClipboardList className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Worker Terminal
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Enterprise Warehouse Picking System
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Updated {getRelativeTime(lastUpdated.toISOString())}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              {statsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              ) : null}
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Picked Today
            </p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {statsLoading ? '-' : stats.pickedToday}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                <Calendar className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Picked Yesterday
            </p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {statsLoading ? '-' : stats.pickedYesterday}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              Orders to Pick
            </p>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              {statsLoading ? '-' : stats.totalToPick}
            </p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
            </div>
            <p className="text-sm font-medium text-white/80 mb-1">Pick Progress</p>
            <p className="text-3xl font-bold text-white">
              {statsLoading ? '-' : `${stats.pickPercentage}%`}
            </p>
            <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${stats.pickPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {claimError && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3 animate-in fade-in duration-200">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{claimError}</p>
          </div>
        )}

        {ordersError && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-300">{ordersError.message}</p>
            </div>
            {ordersError.retryable && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Retry'
                )}
              </Button>
            )}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by order #, customer, SKU..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="flex gap-3">
                <div className="relative">
                  <button
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      {sortBy === 'oldest' ? 'Oldest First' : 'Newest First'}
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </button>

                  {showSortMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowSortMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                        <button
                          onClick={() => {
                            setSortBy('oldest');
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            sortBy === 'oldest'
                              ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          Oldest First (Recommended)
                        </button>
                        <button
                          onClick={() => {
                            setSortBy('newest');
                            setShowSortMenu(false);
                          }}
                          className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            sortBy === 'newest'
                              ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          Newest First
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {searchQuery ? `${filteredOrders.length} found` : `${displayedCount} of ${totalCount}`}
                  </span>
                </div>
              </div>
            </div>

            <ItemCountFilterBar
              selectedFilter={itemCountFilter}
              onFilterChange={handleFilterChange}
              counts={orderCounts}
              loading={countsLoading}
            />
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-cyan-500 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Package className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {searchQuery
                    ? 'No matching orders'
                    : itemCountFilter !== 'all'
                    ? `No ${ITEM_COUNT_FILTER_OPTIONS.find(o => o.value === itemCountFilter)?.label.toLowerCase() || ''} orders`
                    : 'All caught up!'}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : itemCountFilter !== 'all'
                    ? 'Try selecting a different order size filter or check back later.'
                    : 'There are no orders waiting to be picked.'}
                </p>
              </div>
            ) : (
              filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                          {order.order_number}
                        </h3>
                        <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                          Paid
                        </span>
                        {(() => {
                          const fulfillmentLabel = getFulfillmentDisplayLabel(
                            order.financial_status,
                            order.fulfillment_status
                          );
                          const fulfillmentColor = getFulfillmentDisplayColor(
                            order.financial_status,
                            order.fulfillment_status
                          );
                          const colorClass =
                            fulfillmentColor === 'yellow'
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
                          return (
                            <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${colorClass}`}>
                              {fulfillmentLabel}
                            </span>
                          );
                        })()}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-4 w-4" />
                          {getRelativeTime(order.order_date)}
                        </span>
                        <span>
                          {order.customer_name ||
                            `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() ||
                            'Guest'}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Package className="h-4 w-4" />
                          {getTotalUnits(order)} units across {getLineItemCount(order)} items
                        </span>
                      </div>

                      {order.line_items && getPickableLineItems(order.line_items).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {getPickableLineItems(order.line_items).slice(0, 3).map((item, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-lg"
                            >
                              {item.sku || item.title.substring(0, 20)}
                              {item.quantity > 1 && ` x${item.quantity}`}
                            </span>
                          ))}
                          {getPickableLineItems(order.line_items).length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded-lg">
                              +{getPickableLineItems(order.line_items).length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePrintPickSheet(order)}
                        className="flex-shrink-0"
                      >
                        <Printer className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">Print</span>
                      </Button>
                      <Button
                        onClick={() => handleStartPick(order)}
                        disabled={claimingOrderId === order.id}
                        className="flex-shrink-0 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 disabled:opacity-60"
                      >
                        {claimingOrderId === order.id ? (
                          <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">
                          {claimingOrderId === order.id ? 'Claiming...' : 'Pick Order'}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {!loading && filteredOrders.length > 0 && !searchQuery && (
            <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {showingFrom}-{showingTo} of {totalCount} orders
              </p>
              {hasMore && (
                <Button
                  variant="secondary"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More'
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {pickModalOpen && selectedOrder && (
        <PickModal
          order={selectedOrder as any}
          onClose={() => {
            handleReleaseClaim(selectedOrder.id);
            setPickModalOpen(false);
            setSelectedOrder(null);
            refreshOrders();
            refreshCounts();
          }}
          onComplete={handlePickComplete}
        />
      )}

      {printOrder && (
        <PickListPrintable
          order={printOrder as any}
          onClose={() => setPrintOrder(null)}
        />
      )}
    </div>
  );
};

export default WorkerTerminal;
